import { useState } from "react";
import { toast } from "sonner";
import { MessageCircle, Copy, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { apiError } from "@/lib/api";
import { fmtEur, buildWhatsAppText, WHATSAPP_NUMBER } from "@/lib/quote";

export const SummaryStep = ({ data, items, totals, config = {}, saveDraft, resumeToken, submitted, setSubmitted }) => {
  const [ivaIncluded, setIvaIncluded] = useState(true);
  const [sending, setSending] = useState(false);
  const [quoteNumber, setQuoteNumber] = useState(null);

  const submit = async () => {
    if (items.length === 0) return toast.error("Seleziona almeno una voce dal listino");
    setSending(true);
    try {
      const q = await saveDraft("nuovo");
      if (q?.number) setQuoteNumber(q.number);
      const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${buildWhatsAppText(data, items, totals, config)}`;
      window.open(url, "_blank");
      setSubmitted(true);
      toast.success("Preventivo inviato! Si è aperto WhatsApp con il riepilogo.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSending(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/riprendi/${resumeToken}`);
    toast.success("Link copiato! Salvalo per riprendere il preventivo.");
  };

  if (submitted)
    return (
      <div data-testid="wizard-success-screen" className="bg-white rounded-2xl border p-8 text-center space-y-4">
        <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto" />
        <h2 className="font-display text-2xl font-bold">Richiesta inviata!</h2>
        {quoteNumber && (
          <div data-testid="quote-number-display" className="inline-block bg-[#FBF6F0] border border-[#e8ddd0] rounded-full px-5 py-2">
            <span className="text-sm text-[#8a7a6d]">Il tuo preventivo è il </span>
            <span className="font-mono-num font-bold text-[#C85A32]">n. {quoteNumber}</span>
          </div>
        )}
        <p className="text-sm text-[#8a7a6d] max-w-md mx-auto">
          Abbiamo ricevuto la tua richiesta di preventivo. Ti risponderemo al più presto su WhatsApp.
          Dopo l'approvazione riceverai il PDF ufficiale via email con le istruzioni per l'acconto.
        </p>
        <p className="font-mono-num text-3xl font-bold text-[#C85A32]">{fmtEur(totals.total)}</p>
        <p className="text-xs text-[#8a7a6d]">Totale indicativo, IVA inclusa — diventa vincolante solo dopo l'approvazione</p>
      </div>
    );

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border p-6 sm:p-8">
        <h2 className="font-display text-2xl font-bold text-[#2C1A11] mb-1">Riepilogo</h2>
        <p className="text-sm text-[#8a7a6d] mb-5">
          {data.event.type} · {data.event.date} · {data.guests.adults + data.guests.children} ospiti
        </p>
        <div className="divide-y">
          {items.map((it) => (
            <div key={it.product_id} className="py-2.5 flex justify-between text-sm">
              <span>{it.name} <span className="text-[#8a7a6d]">x{it.qty}</span></span>
              <span className="font-mono-num font-semibold">
                {fmtEur(ivaIncluded ? it.price * it.qty : (it.price * it.qty) / (1 + it.vat / 100))}
              </span>
            </div>
          ))}
          {items.length === 0 && <p className="py-4 text-sm text-[#8a7a6d]">Nessuna voce selezionata. Torna indietro per scegliere dal listino.</p>}
        </div>
        <div className="flex items-center justify-between mt-4 pt-4 border-t">
          <div className="flex items-center gap-2 text-sm">
            <Switch data-testid="wizard-iva-toggle" checked={ivaIncluded} onCheckedChange={setIvaIncluded} />
            <span>{ivaIncluded ? "IVA inclusa" : "IVA esclusa"}</span>
          </div>
          <div className="text-right">
            <div className="text-xs text-[#8a7a6d]">Totale indicativo</div>
            <div data-testid="summary-total" className="font-mono-num text-2xl font-bold text-[#C85A32]">
              {fmtEur(ivaIncluded ? totals.total : totals.subtotal_net)}
            </div>
          </div>
        </div>
      </div>

      <Button data-testid="whatsapp-quote-button" disabled={sending} onClick={submit}
        className="w-full h-14 rounded-full bg-[#25D366] hover:bg-[#1eb455] text-white text-base font-bold">
        <MessageCircle className="w-5 h-5 mr-2" /> Invia preventivo su WhatsApp
      </Button>
      <p className="text-xs text-center text-[#8a7a6d]">
        Si aprirà WhatsApp verso il Bliss Café (+39 380 380 5349) con il riepilogo precompilato.
      </p>

      {resumeToken && (
        <button data-testid="copy-resume-link" onClick={copyLink} className="w-full flex items-center justify-center gap-2 text-sm text-[#8a7a6d] hover:text-[#C85A32] py-2">
          <Copy className="w-4 h-4" /> Copia il link personale per riprendere più tardi
        </button>
      )}
    </div>
  );
};
